import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import type { CarouselImageType } from "../utils/types";
import { useDisableBodyScroll } from "../../../common-deprecated/hooks";
import { useUscSelector } from "../redux/store";
import { trackBrowseCarousel, trackCarouselOpenFullScreen } from "../utils/tracking";
import { useBreakpoint } from "../../../common-deprecated/themes/common";
import { DfVehicleStatus } from "../../shared-logic/utils/dfConstants";
import { cashPriceIsPromotion } from "../../shared-logic/utils/uscPriceUtils";
import { getTotalPriceSelector } from "../utils/priceUtils";
import { enable360Spin, getHeaderImageAltTag } from "../utils/pdpUtils";

type UseCarouselType = {
    amountOfImages: number;
    images: CarouselImageType[];
    isFirstElement: boolean;
    isLastElement: boolean;
    altTag: string;
    isMobile: boolean;
    hasPromotion: boolean;
    isReserved: boolean;
    isSold: boolean;
    sharedSliderIndex: number;
    isFullscreen: boolean;
    setIsFullscreen: (value: boolean) => void;
    shouldStartLoading: (index: number) => boolean;
    updateSharedSliderIndex: (newSliderIndex: number) => void;
    openFullScreen: () => void;
};

const useCarousel = (isRetailer: boolean = false, scrollEnabled?: React.MutableRefObject<boolean>): UseCarouselType => {
    const result = useUscSelector((state) => state.vehicle.result);
    const dealer = useUscSelector((state) => state.dealer.dealer);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const totalPrice = useUscSelector(getTotalPriceSelector(), shallowEqual);

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [sharedSliderIndex, setSharedSliderIndex] = useState(0);
    const [wasFullscreen, setWasFullscreen] = useState(false);

    let images: CarouselImageType[] = useMemo(() => result?.images.map(({ url }) => ({ src: url })) || [], [result]);

    const loadedImages = useRef<CarouselImageType[]>([]);

    const isMobile = useBreakpoint("down", "lg");

    useDisableBodyScroll(!isFullscreen);

    const amountOfImages = images.length;
    const isFirstElement = sharedSliderIndex === 0;
    const isLastElement = sharedSliderIndex === amountOfImages - 1;

    const hasPromotion = cashPriceIsPromotion(totalPrice.cashPrice);
    const isReserved = result?.vehicleStatus.code === DfVehicleStatus.Reserved;
    const isSold = result?.vehicleStatus.code === DfVehicleStatus.Sold;

    // Add the first image an extra time when there is a 360 spin because the button icon obscures the image
    if (enable360Spin(result)) {
        images = [images[0], ...images];
    }

    const altTag = getHeaderImageAltTag(result, dealer);

    const openFullScreen = (): void => {
        setIsFullscreen(true);

        if (!isRetailer) {
            trackCarouselOpenFullScreen(vehicleType);
        }
    };

    const updateSharedSliderIndex = (newSliderIndex: number): void => {
        if (newSliderIndex !== sharedSliderIndex) {
            const index =
                newSliderIndex < 0 ? 0 : newSliderIndex >= amountOfImages ? amountOfImages - 1 : newSliderIndex;
            setSharedSliderIndex(index);

            if (!isRetailer) {
                // Track a browse carousel event every time the slider index changes
                const currentImage = images[index];
                trackBrowseCarousel(vehicleType, currentImage.src);
            }
        }
    };

    /**
     * Lazy loading of the next two images in the carousel.
     * We don't want to load all images at once because it would slow down the initial loading time.
     * This only gets used for the main carousel, not the fullscreen carousel - the fullscreen carousel loads all images at once as
     * at that point 1) the page is already loaded and 2) the user shows intent to view all images.
     */
    const shouldStartLoading = useMemo(() => {
        return (index: number): boolean => {
            // Keep images that are already loaded, loaded.
            if (loadedImages.current[index]) return true;

            // We want to load the previous (if it exists), current and next image.
            const nextLoadIndexes = [sharedSliderIndex - 1, sharedSliderIndex, sharedSliderIndex + 1];
            const shouldLoad = nextLoadIndexes.includes(index);

            if (shouldLoad) {
                loadedImages.current[index] = images[index];
            }

            return shouldLoad;
        };
    }, [sharedSliderIndex, images]);

    // When making the screen smaller whilst isFullscreen = true, the fullscreen overlay will close.
    // We setIsFullscreen(false) to make sure the vertical scrolling is enabled once again.
    useEffect(() => {
        if (isMobile) setIsFullscreen(false);
    }, [isMobile]);

    // We disable the arrowKeys. On Edge this seems to be default behavior to scroll through the slider which is not intended.
    useEffect(() => {
        const disableArrowKeys = (key: KeyboardEvent): void => {
            // We only want to disable the arrow keys when the user is not using a modifier key.
            // Otherwise, we're preventing essential browser functionality.
            const isModifierKey = key.altKey || key.ctrlKey || key.metaKey || key.shiftKey;

            if (["ArrowLeft", "ArrowRight"].indexOf(key.code) > -1 && !isModifierKey) {
                key.preventDefault();
            }
        };

        window.addEventListener("keydown", (key) => disableArrowKeys(key));

        return () => {
            window.removeEventListener("keydown", disableArrowKeys);
        };
    }, []);

    useEffect(() => {
        if (!isRetailer) {
            // We only want to trigger .scrollIntoView if the user is using the carousel with one of the buttons.
            // When the user is scrolling or swiping the carousel, we shouldn't trigger scrollIntoView because it overrides the default behavior.
            // wasFullscreen is necessary to determine whether the previous carousel was in fullscreen or not.
            // When entering / closing fullscreen the first time, we need the image to scrollIntoView instantly.
            const isFullscreenAndScrollEnabled = wasFullscreen && isFullscreen && scrollEnabled?.current;
            const isMobileAndScrollEnabled = !wasFullscreen && !isFullscreen && scrollEnabled?.current && isMobile;
            if (!(isFullscreenAndScrollEnabled || isMobileAndScrollEnabled)) {
                document
                    .getElementById(`${isFullscreen ? "slide-fullscreen-" : "slide-pdp-"}${sharedSliderIndex}`)
                    ?.scrollIntoView({
                        // Originally we made use of smooth scrolling here but due to a bug in the Chromium/Webkit
                        // engines, the smooth scrolling resulted in scrolling issue where it kept going to the first slide
                        // when the user was scrolling through the carousel. Read more: https://github.com/facebook/react/issues/23396
                        behavior: "auto",
                        block: "nearest",
                        inline: "center",
                    });
            }
            setWasFullscreen(isFullscreen);
        } else if (!isFullscreen) {
            document.getElementById(`slide-pdp-${sharedSliderIndex}`)?.scrollIntoView({
                block: "nearest",
                inline: "center",
            });
            try {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            } catch (ignored) {
                // Retailer screens (using Tizen 4) don't support the behavior option.
                window.scrollTo(0, 0);
            }
        }
    }, [sharedSliderIndex, isMobile, isFullscreen, wasFullscreen, isRetailer, scrollEnabled]);

    return {
        amountOfImages,
        images,
        isFirstElement,
        isLastElement,
        altTag,
        isMobile,
        isReserved,
        isSold,
        hasPromotion,
        sharedSliderIndex,
        isFullscreen,
        setIsFullscreen,
        shouldStartLoading,
        updateSharedSliderIndex,
        openFullScreen,
    };
};

export default useCarousel;
